/**
 * Realtime — the BranchHub WebSocket client, and the polling fallback contract.
 *
 * WHAT THIS GIVES THE APP: a dispatch offer reaching the phone the moment the
 * dispatcher writes it, instead of up to 10 s later on the tasks poll. On every
 * `tasks` event the task/home queries are invalidated (TanStack refetches
 * immediately for mounted screens), and a fresh offer additionally raises a
 * toast when the app is visible.
 *
 * THE HANDSHAKE, as the backend defines it:
 *   1. POST /v1/realtime/token { branchId, topics } → short-lived HMAC ticket.
 *      The Staff App asks for ['tasks','alerts'] — the two topics its
 *      permissions (`task.view.self`, `alert.view`) can unlock.
 *   2. WebSocket to /v1/realtime/connect?branchId=&token=&topics=&since= which
 *      the Worker forwards to the BranchHub Durable Object. `since` is the last
 *      seen `seq`, so a reconnect replays the gap from the hub's ring buffer
 *      (`welcome.backlog`); `truncated` means the gap is unprovable and the
 *      queries are invalidated wholesale instead.
 *
 * DEGRADE, DON'T BREAK: two things make this a best-effort layer TODAY:
 *   - POST /realtime/token requires `branch.view`, which floor-staff roles do
 *     not hold — the mint 403s for a SERVER/KITCHEN/CASHIER login;
 *   - the API Worker exposes no public route that forwards the WebSocket
 *     upgrade to the Durable Object's /connect.
 *   Either failure lands in FALLBACK state: the existing 10s/20s polling keeps
 *   every screen correct, and the manager retries in the background (60s) so
 *   the socket lights up the moment the backend closes those gaps, with no app
 *   update. Polling is never disabled while the socket is up — the socket is an
 *   accelerator, not the source of truth.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@sonauto/api-client';
import { api, API_BASE_URL } from './api';
import { queryKeys } from './queries';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../components/Toast';

// ---------------------------------------------------------------------------
// Wire types (transcribed from durable/branch-hub.ts)
// ---------------------------------------------------------------------------

export interface HubEvent {
  readonly seq: number;
  readonly type: string;
  readonly topic: string;
  readonly at: number;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly correlationId: string | null;
}

export type RealtimeStatus = 'idle' | 'connecting' | 'open' | 'fallback';

type EventListener = (event: HubEvent) => void;

const TOPICS = ['tasks', 'alerts'] as const;

/** Base reconnect delay; doubles per failure up to MAX, with jitter. */
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
/** After this many consecutive failures the manager settles into fallback… */
const FAILURES_BEFORE_FALLBACK = 4;
/** …and keeps probing this often so a fixed backend is picked up unattended. */
const FALLBACK_RETRY_MS = 60_000;
const PING_INTERVAL_MS = 25_000;

// ---------------------------------------------------------------------------
// Frame parsing — strict, no `any`
// ---------------------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseHubEvent(value: unknown): HubEvent | null {
  const record = asRecord(value);
  if (record === null) return null;
  if (typeof record.seq !== 'number' || typeof record.type !== 'string' || typeof record.topic !== 'string') {
    return null;
  }
  return {
    seq: record.seq,
    type: record.type,
    topic: record.topic,
    at: typeof record.at === 'number' ? record.at : Date.now(),
    payload: asRecord(record.payload) ?? {},
    correlationId: typeof record.correlationId === 'string' ? record.correlationId : null,
  };
}

function wsUrl(branchId: string, token: string, since: number | null): string {
  const base = API_BASE_URL.startsWith('http')
    ? API_BASE_URL.replace(/^http/, 'ws')
    : `${window.location.origin.replace(/^http/, 'ws')}${API_BASE_URL}`;
  const params = new URLSearchParams({ branchId, token, topics: TOPICS.join(',') });
  if (since !== null) params.set('since', String(since));
  return `${base}/v1/realtime/connect?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Manager (module singleton — one socket per app, however many screens)
// ---------------------------------------------------------------------------

class RealtimeManager {
  #socket: WebSocket | null = null;
  #status: RealtimeStatus = 'idle';
  #branchId: string | null = null;
  #lastSeq: number | null = null;
  #failures = 0;
  #reconnectTimer: number | null = null;
  #pingTimer: number | null = null;
  #generation = 0;
  readonly #listeners = new Set<EventListener>();
  readonly #statusListeners = new Set<(status: RealtimeStatus) => void>();

  get status(): RealtimeStatus {
    return this.#status;
  }

  subscribe(listener: EventListener): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  subscribeStatus(listener: (status: RealtimeStatus) => void): () => void {
    this.#statusListeners.add(listener);
    return () => {
      this.#statusListeners.delete(listener);
    };
  }

  start(branchId: string): void {
    if (this.#branchId === branchId && this.#status !== 'idle') return;
    this.stop();
    this.#branchId = branchId;
    this.#failures = 0;
    void this.#connect();
  }

  stop(): void {
    this.#generation += 1;
    if (this.#reconnectTimer !== null) window.clearTimeout(this.#reconnectTimer);
    if (this.#pingTimer !== null) window.clearInterval(this.#pingTimer);
    this.#reconnectTimer = null;
    this.#pingTimer = null;
    if (this.#socket !== null) {
      try {
        this.#socket.close(1000, 'app stopped');
      } catch {
        /* already closed */
      }
      this.#socket = null;
    }
    this.#branchId = null;
    this.#setStatus('idle');
  }

  #setStatus(status: RealtimeStatus): void {
    if (this.#status === status) return;
    this.#status = status;
    for (const listener of this.#statusListeners) listener(status);
  }

  async #connect(): Promise<void> {
    const branchId = this.#branchId;
    if (branchId === null) return;
    const generation = this.#generation;
    this.#setStatus('connecting');

    let token: string;
    try {
      const minted = await api.realtimeToken(branchId, [...TOPICS]);
      token = minted.token;
    } catch (error) {
      // 403 = this role cannot mint a realtime ticket at all (the endpoint is
      // gated on `branch.view` today). Not transient — settle into fallback
      // with the slow probe rather than hammering a permission error.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        this.#scheduleReconnect(FALLBACK_RETRY_MS, true);
        return;
      }
      this.#failures += 1;
      this.#scheduleReconnect(this.#backoffMs(), this.#failures >= FAILURES_BEFORE_FALLBACK);
      return;
    }
    if (generation !== this.#generation) return;

    let socket: WebSocket;
    try {
      socket = new WebSocket(wsUrl(branchId, token, this.#lastSeq));
    } catch {
      this.#failures += 1;
      this.#scheduleReconnect(this.#backoffMs(), this.#failures >= FAILURES_BEFORE_FALLBACK);
      return;
    }
    this.#socket = socket;

    socket.onopen = () => {
      if (generation !== this.#generation) return;
      this.#failures = 0;
      this.#setStatus('open');
      if (this.#pingTimer !== null) window.clearInterval(this.#pingTimer);
      this.#pingTimer = window.setInterval(() => {
        try {
          socket.send(JSON.stringify({ op: 'ping' }));
        } catch {
          /* the close handler reconnects */
        }
      }, PING_INTERVAL_MS);
    };

    socket.onmessage = (message: MessageEvent<unknown>) => {
      if (generation !== this.#generation) return;
      if (typeof message.data !== 'string') return;
      let frame: unknown;
      try {
        frame = JSON.parse(message.data);
      } catch {
        return;
      }
      const record = asRecord(frame);
      if (record === null || typeof record.op !== 'string') return;

      if (record.op === 'event') {
        const event = parseHubEvent(record.event);
        if (event !== null) this.#deliver(event);
        return;
      }
      if (record.op === 'welcome' || record.op === 'backlog') {
        // ?since= catch-up: the hub replays what this client missed. A
        // truncated backlog means the gap outgrew the ring buffer — deliver a
        // synthetic marker so the hook can invalidate everything.
        if (typeof record.seq === 'number') this.#lastSeq = record.seq;
        const backlogSource = record.op === 'welcome' ? asRecord(record.backlog) : record;
        if (backlogSource !== null) {
          const events = Array.isArray(backlogSource.events) ? backlogSource.events : [];
          for (const raw of events) {
            const event = parseHubEvent(raw);
            if (event !== null) this.#deliver(event);
          }
          if (backlogSource.truncated === true) {
            this.#deliver({
              seq: this.#lastSeq ?? 0,
              type: 'hub.backlog_truncated',
              topic: 'tasks',
              at: Date.now(),
              payload: {},
              correlationId: null,
            });
          }
        }
      }
      // 'pong', 'subscribed', 'error' need no action here.
    };

    const onGone = (): void => {
      if (generation !== this.#generation) return;
      if (this.#pingTimer !== null) window.clearInterval(this.#pingTimer);
      this.#pingTimer = null;
      this.#socket = null;
      this.#failures += 1;
      this.#scheduleReconnect(this.#backoffMs(), this.#failures >= FAILURES_BEFORE_FALLBACK);
    };
    socket.onclose = onGone;
    socket.onerror = () => {
      try {
        socket.close();
      } catch {
        /* already closed */
      }
    };
  }

  #deliver(event: HubEvent): void {
    this.#lastSeq = Math.max(this.#lastSeq ?? 0, event.seq);
    for (const listener of this.#listeners) listener(event);
  }

  #backoffMs(): number {
    const exp = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.min(this.#failures - 1, 5));
    return exp + Math.trunc(Math.random() * 500);
  }

  #scheduleReconnect(delayMs: number, fallback: boolean): void {
    this.#setStatus(fallback ? 'fallback' : 'connecting');
    if (this.#reconnectTimer !== null) window.clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = window.setTimeout(
      () => void this.#connect(),
      fallback ? FALLBACK_RETRY_MS : delayMs,
    );
  }
}

export const realtime = new RealtimeManager();

/** Screens (e.g. Chat) refetch instantly when ANY branch event arrives. */
export function subscribeRealtime(listener: EventListener): () => void {
  return realtime.subscribe(listener);
}

// ---------------------------------------------------------------------------
// The one hook the app mounts
// ---------------------------------------------------------------------------

/**
 * Wires the socket to the query cache and the toaster. Mounted once, in
 * `SignedInRoutes`. Polling on the task/home queries stays on regardless — see
 * the module header for why realtime is additive, not a replacement.
 */
export function useRealtime(): void {
  const { branch, can } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const branchId = branch?.id ?? null;
  const canTasks = can('task.view.self') || can('task.view');

  useEffect(() => {
    if (branchId === null || !canTasks) return;
    realtime.start(branchId);

    const unsubscribe = realtime.subscribe((event) => {
      if (event.topic === 'tasks') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
        void queryClient.invalidateQueries({ queryKey: queryKeys.home });
        if (event.type === 'hub.backlog_truncated') {
          // The gap is unprovable — refetch everything staff-scoped.
          void queryClient.invalidateQueries({ queryKey: ['staff'] });
          return;
        }
        // A fresh offer, surfaced the second it lands — with whatever
        // countdown the payload carries. Only when the app is on screen: a
        // backgrounded PWA cannot show UI, and the notification row covers it.
        if (event.type === 'task.offered' && document.visibilityState === 'visible') {
          const title = typeof event.payload.title === 'string'
            ? event.payload.title
            : typeof event.payload.title_vi === 'string'
              ? event.payload.title_vi
              : 'Nhiệm vụ mới';
          const expiresAt =
            typeof event.payload.offerExpiresAt === 'number'
              ? event.payload.offerExpiresAt
              : typeof event.payload.expiresAt === 'number'
                ? event.payload.expiresAt
                : null;
          const secondsLeft = expiresAt === null ? null : Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
          toast.info(
            `Đang mời bạn nhận: ${title}`,
            secondsLeft === null
              ? 'Mở tab Công việc để phản hồi.'
              : `Còn khoảng ${secondsLeft} giây để phản hồi — mở tab Công việc.`,
          );
        }
        return;
      }
      if (event.topic === 'alerts') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
        void queryClient.invalidateQueries({ queryKey: queryKeys.home });
      }
    });

    return () => {
      unsubscribe();
      realtime.stop();
    };
  }, [branchId, canTasks, queryClient, toast]);
}
